<template>
    <div data-spec-class="field">
        <div
            :class="[
                labelWrapperClass,
                {
                    'flex items-center gap-1.5': isInline,
                },
            ]"
        >
            <div
                v-if="label || isOptional"
                class="flex justify-between"
            >
                <!-- eslint-disable vue/no-v-html -->
                <label
                    v-if="label"
                    :for="name"
                    :class="classObject"
                    data-spec-class="field__label"
                    @click="emits('label-click')"
                    v-html="label"
                >
                </label>
                <!-- eslint-enable vue/no-v-html -->
                <span
                    v-if="isOptional"
                    class="text-sm text-muted"
                    :class="{
                        '!text-gray-300': customThemeBase === FormTheme.Website,
                    }"
                    data-spec-class="field__optional"
                >
                    {{ t('forms.optional') }}
                </span>
            </div>
            <slot> </slot>
        </div>

        <div
            v-if="hint"
            class="mt-2 text-sm"
            :class="{
                'text-muted': customThemeBase === FormTheme.CustomerArea,
                'text-gray-300': customThemeBase === FormTheme.Website,
            }"
            data-spec-class="field__hint"
        >
            {{ hint }}
        </div>

        <div
            v-if="errorMessages.length"
            class="mt-2 text-sm text-red"
            data-spec-class="field__error"
        >
            <ul>
                <li
                    v-for="(errorMessage, i) in errorMessages"
                    :key="i"
                >
                    {{ errorMessage }}
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import { withDefaults, defineProps, defineEmits, computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { FormTheme } from '@/types';

const emits = defineEmits(['update:modelValue', 'label-click']);

const { t } = useI18n();

interface Props {
    labelWrapperClass?: string;
    errorMessages?: string[];
    name: string;
    isOptional?: boolean;
    label?: string;
    hint?: string;
    customThemeBase?: FormTheme;
    isInline?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    labelWrapperClass: '',
    hint: '',
    isOptional: false,
    vuelidateField: undefined,
    errorMessages: () => [],
    customThemeBase: FormTheme.CustomerArea,
    isInline: false,
});

const instance = getCurrentInstance();
const isLabelClickable = computed(() => Boolean(instance?.vnode?.props?.onLabelClick));

const themeBase = computed(() => {
    if (props.customThemeBase === FormTheme.Website) {
        return 'mb-2 block pl-5 text-left text-white';
    }

    if (props.isInline) {
        return 'text-sm font-medium text-tertiary whitespace-nowrap';
    }

    return 'block text-sm font-medium text-tertiary mb-1';
});

const classObject = computed(() => ({
    [themeBase.value]: true,
    'cursor-pointer hover:underline hover:text-primary': isLabelClickable.value,
}));
</script>
